import React, { useState, useEffect } from 'react';
import { buildingData, defaultInitialState, researchData, shipData, defenseData } from '../data';

const SettingsModal = ({ initialState, onSave, onClose }) => {
  const [newState, setNewState] = useState(JSON.parse(JSON.stringify(initialState)));
  const [activeSection, setActiveSection] = useState('general');

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Helper functions to extract date and time parts from DateTime
  const getDateValue = (date) => {
    const d = new Date((new Date(date)).getTime() + new Date().getTimezoneOffset() * -60 * 1000);
    return d.toISOString().slice(0, 10); // YYYY-MM-DD
  }

  const getTimeValue = (date) => {
    const d = new Date((new Date(date)).getTime() + new Date().getTimezoneOffset() * -60 * 1000);
    return d.toISOString().slice(11, 19); // HH:MM:SS
  }
  // Handle resource changes (temperature and resources)
  const handleResourceChange = (e) => {
    const { name, value } = e.target;
    const planetId = e.target.getAttribute('data-planetid');
    const parsedValue = parseInt(value) || 0;

    setNewState((prev) => {
      const newStateObj = {...prev};

      if (!newStateObj.planets[planetId]) return prev;

      if (name === "temperature") {
        newStateObj.planets[planetId] = {
          ...newStateObj.planets[planetId],
          temperature: parsedValue
        };
      } else {
        newStateObj.planets[planetId] = {
          ...newStateObj.planets[planetId],
          resources: {
            ...newStateObj.planets[planetId].resources,
            [name]: parsedValue
          }
        };
      }

      return newStateObj;
    });
  };

  // Handle building level changes
  const handleBuildingChange = (e) => {
    const { name, value } = e.target;
    const planetId = e.target.getAttribute('data-planetid');
    const parsedValue = parseInt(value) || 0;

    setNewState((prev) => {
      const newStateObj = {...prev};

      if (!newStateObj.planets[planetId]) return prev;

      newStateObj.planets[planetId] = {
        ...newStateObj.planets[planetId],
        buildings: {
          ...newStateObj.planets[planetId].buildings,
          [name]: parsedValue
        }
      };

      return newStateObj;
    });
  };

  // Handle ship count changes
  const handleShipChange = (e) => {
    const { name, value } = e.target;
    const planetId = e.target.getAttribute('data-planetid');
    const parsedValue = parseInt(value) || 0;

    setNewState((prev) => {
      const newStateObj = {...prev};

      if (!newStateObj.planets[planetId]) return prev;

      newStateObj.planets[planetId] = {
        ...newStateObj.planets[planetId],
        ships: {
          ...newStateObj.planets[planetId].ships,
          [name]: parsedValue
        }
      };

      return newStateObj;
    });
  };

  // Handle defense count changes
  const handleDefenseChange = (e) => {
    const { name, value } = e.target;
    const planetId = e.target.getAttribute('data-planetid');
    const parsedValue = parseInt(value) || 0;

    setNewState((prev) => {
      const newStateObj = {...prev};

      if (!newStateObj.planets[planetId]) return prev;

      newStateObj.planets[planetId] = {
        ...newStateObj.planets[planetId],
        defenses: {
          ...newStateObj.planets[planetId].defenses,
          [name]: parsedValue
        }
      };

      return newStateObj;
    });
  };

  // Handle research level changes
  const handleResearchChange = (e) => {
    const { name, value } = e.target;
    const parsedValue = parseInt(value) || 0;

    setNewState((prev) => {
      return {
        ...prev,
        research: {
          ...prev.research,
          [name]: parsedValue
        }
      };
    });
  };

  const handleDateChange = (e) => {
    const { value } = e.target;
    setNewState((prev) => {
      // Keep the time part, update only the date part
      const currentTime = getTimeValue(prev.startTime);
      const newDateTime = new Date(`${value}T${currentTime}`);

      return {
        ...prev,
        startTime: newDateTime
      };
    });
  };

  const handleTimeChange = (e) => {
    const { value } = e.target;
    setNewState((prev) => {
      // Keep the date part, update only the time part
      const currentDate = getDateValue(prev.startTime);
      const newDateTime = new Date(`${currentDate}T${value}`);

      return {
        ...prev,
        startTime: newDateTime
      };
    });
  };

  // Handle speed setting changes (decimal multipliers, unlike the integer fields above)
  const handleSpeedChange = (e) => {
    const { name, value } = e.target;
    const parsedValue = parseFloat(value) || 0;

    setNewState((prev) => ({
      ...prev,
      [name]: parsedValue
    }));
  };

  const handleSave = () => {
    onSave(newState);
  };

  const handleReset = () => {
    setNewState(defaultInitialState);
    setActiveSection('general');
  };

  return (
    <div
      className="fixed inset-0 bg-gray-900 z-50 bg-opacity-50 flex justify-center items-center"
      onClick={onClose}
    >
      <div
        className="bg-gray-800 rounded-lg shadow-lg w-3/4 h-[70vh] flex flex-col text-white"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 flex items-center justify-between border-b border-gray-600">
          <h2 className="text-lg font-bold text-white">Start Settings</h2>
          <button onClick={handleReset} className="bg-blue-500 text-white py-1 px-4 rounded">Reset</button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Left nav */}
          <div className="w-40 bg-gray-700 overflow-y-auto flex-shrink-0">
            <div
              onClick={() => setActiveSection('general')}
              className={`p-2 cursor-pointer text-white ${activeSection === 'general' ? 'bg-gray-600' : 'hover:bg-gray-600'}`}
            >
              General
            </div>
            <div
              onClick={() => setActiveSection('research')}
              className={`p-2 cursor-pointer text-white ${activeSection === 'research' ? 'bg-gray-600' : 'hover:bg-gray-600'}`}
            >
              Research
            </div>
            <div className="border-t border-gray-600 my-2" />
            {Object.keys(newState.planets).map((planetId) => (
              <div
                key={planetId}
                onClick={() => setActiveSection(planetId)}
                className={`p-2 cursor-pointer text-white ${activeSection === planetId ? 'bg-gray-600' : 'hover:bg-gray-600'}`}
              >
                {newState.planets[planetId].name}
              </div>
            ))}
          </div>

          {/* Right content pane */}
          <div className="flex-1 overflow-y-auto p-4">
            {activeSection === 'general' && (
              <div>
                <div className="grid grid-cols-2 gap-4">
                  <label className="text-left text-gray-200">Startdatum:</label>
                  <input
                    name="startDate"
                    type="date"
                    value={getDateValue(newState.startTime)}
                    onChange={handleDateChange}
                    className="w-full p-2 border-none rounded-md bg-gray-700 text-white"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4 mt-2">
                  <label className="text-left text-gray-200">Startzeit:</label>
                  <input
                    name="startTime"
                    type="time"
                    value={getTimeValue(newState.startTime)}
                    onChange={handleTimeChange}
                    className="w-full p-2 border-none rounded-md bg-gray-700 text-white"
                    step="1"
                  />
                </div>

                <h4 className="font-semibold mb-2 mt-4 text-left text-gray-100">Speed</h4>
                <div className="grid grid-cols-2 gap-4">
                  <label className="text-left text-gray-200">Resource Speed:</label>
                  <input
                    name="ressSpeed"
                    type="number"
                    step="0.1"
                    value={newState.ressSpeed}
                    onChange={handleSpeedChange}
                    className="w-full p-2 rounded-md bg-gray-700 text-white"
                  />
                  <label className="text-left text-gray-200">Build Speed:</label>
                  <input
                    name="buildSpeed"
                    type="number"
                    step="0.1"
                    value={newState.buildSpeed}
                    onChange={handleSpeedChange}
                    className="w-full p-2 rounded-md bg-gray-700 text-white"
                  />
                  <label className="text-left text-gray-200">Research Speed:</label>
                  <input
                    name="researchSpeed"
                    type="number"
                    step="0.1"
                    value={newState.researchSpeed}
                    onChange={handleSpeedChange}
                    className="w-full p-2 rounded-md bg-gray-700 text-white"
                  />
                </div>
              </div>
            )}

            {activeSection === 'research' && (
              <div className="grid grid-cols-1 gap-4">
                <MakeResearchInputs research={newState.research} onChange={handleResearchChange} />
              </div>
            )}

            {activeSection !== 'general' && activeSection !== 'research' && newState.planets[activeSection] && (
              (() => {
                const planetId = activeSection;
                const planet = newState.planets[planetId];
                return (
                  <div>
                    <div className="grid grid-cols-2 gap-4">
                      <label className="text-left text-gray-200">Temperature:</label>
                      <input
                        type="number"
                        name="temperature"
                        data-planetid={planetId}
                        value={planet.temperature}
                        onChange={handleResourceChange}
                        className="p-1 rounded w-full bg-gray-700 text-white"
                      />
                    </div>

                    <h4 className="font-semibold mb-2 mt-4 text-left text-gray-100">Resources</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <label className="text-left text-gray-200">Metal:</label>
                      <input
                        type="number"
                        name="metal"
                        data-planetid={planetId}
                        value={planet.resources.metal}
                        onChange={handleResourceChange}
                        className="p-1 rounded w-full bg-gray-700 text-white"
                      />
                      <label className="text-left text-gray-200">Crystal:</label>
                      <input
                        type="number"
                        name="crystal"
                        data-planetid={planetId}
                        value={planet.resources.crystal}
                        onChange={handleResourceChange}
                        className="p-1 rounded w-full bg-gray-700 text-white"
                      />
                      <label className="text-left text-gray-200">Deuterium:</label>
                      <input
                        type="number"
                        name="deuterium"
                        data-planetid={planetId}
                        value={planet.resources.deuterium}
                        onChange={handleResourceChange}
                        className="p-1 rounded w-full bg-gray-700 text-white"
                      />
                    </div>

                    <h4 className="font-semibold mb-2 mt-4 text-left text-gray-100">Buildings</h4>
                    <div className="grid grid-cols-1 gap-4">
                      <MakeBuildingInputs planetId={planetId} buildings={planet.buildings} onChange={handleBuildingChange} />
                    </div>

                    <h4 className="font-semibold mb-2 mt-4 text-left text-gray-100">Fleet</h4>
                    <div className="grid grid-cols-1 gap-4">
                      <MakeShipInputs planetId={planetId} ships={planet.ships} onChange={handleShipChange} />
                    </div>

                    <h4 className="font-semibold mb-2 mt-4 text-left text-gray-100">Defense</h4>
                    <div className="grid grid-cols-1 gap-4">
                      <MakeDefenseInputs planetId={planetId} defenses={planet.defenses} onChange={handleDefenseChange} />
                    </div>
                  </div>
                );
              })()
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-2 p-4 border-t border-gray-600">
          <button onClick={handleSave} className="bg-blue-500 text-white py-1 px-4 rounded">
            Save
          </button>
          <button onClick={onClose} className="bg-gray-600 hover:bg-gray-500 text-white py-1 px-4 rounded">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const MakeBuildingInputs = ({ planetId, buildings, onChange }) => {
  return Object.keys(buildings).map((building) => {
    return (
      <div key={building + planetId} className="grid grid-cols-2 gap-4">
        <label className="text-left text-gray-200">{buildingData[building].name}</label>
        <input
          type="number"
          name={building}
          data-planetid={planetId}
          value={buildings[building]}
          onChange={onChange}
          className="p-1 rounded w-full bg-gray-700 text-white"
        />
      </div>
    )
  });
}

const MakeShipInputs = ({ planetId, ships, onChange }) => {
  return Object.keys(ships).map((ship) => {
    return (
      <div key={ship + planetId} className="grid grid-cols-2 gap-4">
        <label className="text-left text-gray-200">{shipData[ship].name}</label>
        <input
          type="number"
          name={ship}
          data-planetid={planetId}
          value={ships[ship]}
          onChange={onChange}
          className="p-1 rounded w-full bg-gray-700 text-white"
        />
      </div>
    )
  });
}

const MakeDefenseInputs = ({ planetId, defenses, onChange }) => {
  return Object.keys(defenses).map((defense) => {
    return (
      <div key={defense + planetId} className="grid grid-cols-2 gap-4">
        <label className="text-left text-gray-200">{defenseData[defense].name}</label>
        <input
          type="number"
          name={defense}
          data-planetid={planetId}
          value={defenses[defense]}
          onChange={onChange}
          className="p-1 rounded w-full bg-gray-700 text-white"
        />
      </div>
    )
  });
}

const MakeResearchInputs = ({ research, onChange }) => {
  return Object.keys(research).map((tech) => {
    return (
      <div key={tech} className="grid grid-cols-2 gap-4">
        <label className="text-left text-gray-200">{researchData[tech].name}</label>
        <input
          type="number"
          name={tech}
          value={research[tech]}
          onChange={onChange}
          className="p-1 rounded w-full bg-gray-700 text-white"
        />
      </div>
    )
  });
}

export default SettingsModal;
